import { useEffect, useState } from 'react';

const clamp = (v, d) => (v === '' || !Number.isFinite(+v) ? d : Math.min(2000, Math.max(20, Math.round(+v))));
import { toast } from 'sonner';
import { Sparkles, Check, X } from 'lucide-react';
import { uid } from '../lib/model';
import { suggestLoggers, SPACING, NIGHT } from '../lib/loggerSuggest';
import { LST } from '../lib/loggerSuggestText';
import { openNetShare } from './NetShare';

const label = (x, s) => (s.name && s.name.toLowerCase().startsWith(x.kinds[s.kind].toLowerCase()) ? s.name : `${x.kinds[s.kind]}${s.name ? ` · ${s.name}` : ''}`);

export const showSuggest = (list) => window.dispatchEvent(new CustomEvent('giss-lp-suggest', { detail: list }));

// logger plan: suggestion along the network -> preview on map + list -> take all / selected
const fix = (o) => Object.fromEntries(Object.keys(SPACING).map((k) => [k, clamp(o[k], SPACING[k])]));

function Spacing({ x, v, setV, set }) {
  const is = (o) => Object.keys(o).every((k) => +v[k] === o[k]), save = (o) => { set(fix(o)); setV(null); };
  return (
    <div className="lp-sp" data-testid="lp-spacing">
      <div className="lp-row">
        <button type="button" className={`chip seg ${is(SPACING) ? 'on' : ''}`} data-testid="lp-sp-normal" onClick={() => save(SPACING)}>{x.spNormal}</button>
        <button type="button" className={`chip seg ${is(NIGHT) ? 'on' : ''}`} data-testid="lp-sp-night" onClick={() => save(NIGHT)}>🌙 {x.spNight}</button>
      </div>
      <div className="lp-sp-in">
        {['metal', 'plastic', 'other'].map((k) => <label key={k}>{x[`sp_${k}`]}<span><input type="number" min="20" max="2000" step="10" inputMode="numeric" data-testid={`lp-sp-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={() => save(v)} /> m</span></label>)}
      </div>
    </div>
  );
}

export default function LoggerSuggest({ lang, project, plan, setPlan, mapRef, md, setSpacing }) {
  const x = LST[lang] || LST.en, [list, setList] = useState(null), [off, setOff] = useState(new Set()), [view, setView] = useState(true);
  useEffect(() => () => showSuggest([]), []);
  useEffect(() => { if (list) showSuggest(list.filter((z) => !off.has(z.id))); }, [list, off]);
  const [draft, setDraft] = useState(null), sp = draft || { ...SPACING, ...(project.lgSpacing || {}) };
  if (!(project.pipes || []).length) return <button className="btn btn-outline" data-testid="lp-suggest-nonet" onClick={openNetShare}><Sparkles size={15} /> {x.noNet}</button>;
  const calc = () => { const r = suggestLoggers(project, view ? mapRef.current?.getBounds?.() : null, fix(sp)); setList(r); setOff(new Set()); if (!r.length) toast.info(x.none); };
  const close = () => { setList(null); showSuggest([]); };
  const take = (items) => {
    setPlan([...plan, ...items.map((s, i) => ({ id: uid('lp'), name: s.name ? label(x, s) : `${x.kinds[s.kind]} ${plan.length + i + 1}`, address: s.kind === 'gap' ? x.gap : '', lat: s.lat, lon: s.lon, mode: md.mode, days: md.days, ...(s.fid ? { fid: s.fid } : {}) }))]);
    toast.success(x.added.replace('{n}', items.length)); close();
  };
  const on = list ? list.filter((s) => !off.has(s.id)) : [];
  return (
    <div className="lp-sugg" data-testid="lp-suggest">
      {!list && <>
        <small className="muted">{x.hint}</small>
        <Spacing x={x} v={sp} setV={setDraft} set={setSpacing} />
        <label className="lp-sugg-view"><input type="checkbox" data-testid="lp-suggest-view" checked={view} onChange={(e) => setView(e.target.checked)} /> {x.view}</label>
        <button className="btn btn-outline" data-testid="lp-suggest-btn" onClick={calc}><Sparkles size={15} /> {x.btn}</button>
      </>}
      {list && <>
        <b data-testid="lp-suggest-count">{x.n.replace('{n}', list.length)}</b>
        <ul className="lp-sugg-list" data-testid="lp-suggest-list">
          {list.map((s, i) => (
            <li key={s.id}>
              <label><input type="checkbox" data-testid={`lp-suggest-item-${i}`} checked={!off.has(s.id)} onChange={() => { const n = new Set(off); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); setOff(n); }} />
                <span className={`lp-sugg-k k-${s.kind}`}>{x.kinds[s.kind]}</span> <span>{(s.name || '–').replace(new RegExp(`^${x.kinds[s.kind]}\\s*`, 'i'), '')}</span> <small className="muted">{s.spacing} m{s.kind === 'gap' ? ` · ${x.gap}` : ''}</small></label>
              <button className="icon-btn" aria-label="zoom" data-testid={`lp-suggest-zoom-${i}`} onClick={() => mapRef.current.setView(s.lat, s.lon, 19)}>◎</button>
            </li>
          ))}
        </ul>
        <div className="lp-row">
          <button className="btn btn-green" data-testid="lp-suggest-all" disabled={!list.length} onClick={() => take(list)}><Check size={15} /> {x.all}</button>
          {off.size > 0 && <button className="btn btn-primary" data-testid="lp-suggest-sel" disabled={!on.length} onClick={() => take(on)}>{x.sel.replace('{n}', on.length)}</button>}
          <button className="btn btn-outline" data-testid="lp-suggest-drop" onClick={close}><X size={15} /> {x.drop}</button>
        </div>
      </>}
    </div>
  );
}
